// apps/web/src/features/russia-requests/cacheNarrowing.ts
// Russia-side realtime cache-narrowing helpers (#1664). Split out of
// `queries.ts` (file-size boundary) as a cohesive responsibility: these are
// the row-/id-scoped invalidation predicates the realtime handlers call,
// distinct from the CRUD hooks and key factory. They mirror the China twins
// (`narrowInvalidateForRequest` / `invalidateUnreadSummariesContaining`).
import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { russiaRequestsKeys } from './queryKeys.js';

// WHY (#1664): the China twin reuses `requestListDataHasRow` from the requests
//   feature, but the web feature-capsule boundary forbids a russia-requests →
//   requests cross-feature import. The check is a trivial structural shape
//   probe over the identical `{ items: [{ id }] }` list wire (single-page OR
//   `InfiniteData<>`), so it is inlined here rather than punching a hole in
//   the capsule contract.
function listDataHasRow(data: unknown, id: string): boolean {
  type ListPage = { items?: ReadonlyArray<{ id: string }> };
  if (!data || typeof data !== 'object') return false;
  if ('pages' in data) {
    const pages = (data as InfiniteData<ListPage>).pages;
    return pages.some((p) => p.items?.some((r) => r.id === id) ?? false);
  }
  if ('items' in data) {
    return (data as ListPage).items?.some((r) => r.id === id) ?? false;
  }
  return false;
}

/**
 * @aiInvariant Russia twin of China's `narrowInvalidateForRequest` (#1664):
 *   narrowly invalidate caches for a single CHANGED/CREATED RF request —
 *   always its detail, plus only the list queries whose cache currently
 *   CONTAINS the row (membership-loss/update). Never invalidates the whole
 *   `['russia-requests']` prefix. Russia has no `unread=true` list filter, so
 *   (unlike China) there is no unread-membership-gain branch.
 *   ACCEPTED BEHAVIOR CHANGE (#1664, mirrors China #1593): a `created` row no
 *   longer forces refetch of lists that don't contain it — discovery happens
 *   via lifecycle catch-up on focus/online (`invalidateRequestCaches`).
 */
export function narrowInvalidateForRussiaRequest(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({ queryKey: russiaRequestsKeys.detail(id) });
  void qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as readonly unknown[];
      if (key[0] !== 'russia-requests') return false;
      return listDataHasRow(q.state.data, id);
    },
  });
}

/**
 * @aiInvariant Russia twin of China's `invalidateUnreadSummariesContaining`
 *   (#1664, PERF-10/RT-2): a chat/read event names ONE russiaRequestId, but the
 *   summary key embeds an id SET
 *   (`['russia-request-chat-unread-summary', sortedIds]`). Invalidate only the
 *   summary queries whose key id-array CONTAINS the affected id rather than the
 *   whole prefix.
 */
export function invalidateRussiaUnreadSummariesContaining(qc: QueryClient, id: string): void {
  void qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey as readonly unknown[];
      if (key[0] !== 'russia-request-chat-unread-summary') return false;
      const ids = key[1];
      return Array.isArray(ids) && ids.includes(id);
    },
  });
}
